import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Upload, Sparkles, FileText, Loader2, AlertCircle, FileUp, X, CheckCircle2, RotateCw,
    BadgeCheck, MessageSquare, Mic, TrendingUp, Compass, Linkedin, Search, Eye, ShieldCheck,
    Zap, IdCard, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PassportCard, PASSPORT_PEOPLE } from "@/components/platform/PassportCard";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { SocialProof } from "@/components/marketing/SocialProof";
import { TID } from "@/lib/testIds";

const passportContents = [
    "Professional Profile", "Resume", "AI Career Summary", "Skills", "Work History", "Certifications",
    "Languages", "Career Preferences", "Salary Expectations", "Availability", "Verification checklist", "Video Introduction",
];

const coachFeatures = [
    { icon: FileText, label: "Generate cover letters" },
    { icon: Mic, label: "Practice interviews" },
    { icon: MessageSquare, label: "Improve interview answers" },
    { icon: TrendingUp, label: "Salary negotiation coaching" },
    { icon: Compass, label: "Career planning" },
    { icon: Linkedin, label: "LinkedIn profile suggestions" },
    { icon: Search, label: "Job search strategies" },
];

const benefits = [
    "Apply faster", "Get discovered by employers", "AI resume optimization", "Personalized job matching",
    "AI interview coaching", "Career insights", "Secure professional profile", "Complete your verifications",
];

const trustChecks = ["Email", "Phone", "Identity", "Employment", "References"];

const coachChat = [
    { from: "user", text: "I have an interview for an HR Manager role Thursday. Help me prepare?" },
    { from: "ai", text: "Great — let's run a mock interview. First question: tell me about a time you resolved a complex employee-relations issue. I'll score your answer on structure, impact, and specifics." },
    { from: "user", text: "Can you also tailor my resume for this role?" },
    { from: "ai", text: "Done — I emphasized your ADP Workforce Now experience and California labor-law knowledge, and added 3 quantified achievements. Want a matching cover letter?" },
];

const JobSeekers = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [uploadLoading, setUploadLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [aiResult, setAiResult] = useState(null);
    const [resumeText, setResumeText] = useState("");
    const [searchParams] = useSearchParams();
    const reuploadToken = searchParams.get("reupload") || "";
    const [reuploadStatus, setReuploadStatus] = useState({ state: "idle", info: null });
    const isReupload = !!reuploadToken;
    const reuploadEmail = searchParams.get("email") || "";

    const fileInputRef = useRef(null);
    const optimizerFileRef = useRef(null);
    const [resumeFile, setResumeFile] = useState(null);
    const [workAuth, setWorkAuth] = useState("");
    const [availability, setAvailability] = useState("");
    const [yearsExp, setYearsExp] = useState("");
    const [jobPrefs, setJobPrefs] = useState({ Onsite: false, Hybrid: false, Remote: false });
    const [prefillEmail, setPrefillEmail] = useState("");

    useEffect(() => {
        if (!isReupload) return;
        setPrefillEmail(reuploadEmail);
        setTimeout(() => {
            const el = document.getElementById("passport-form");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
        setReuploadStatus({ state: "verifying", info: null });
        api.get(`/reupload/verify/${reuploadToken}`)
            .then(({ data }) => {
                setReuploadStatus({ state: "valid", info: data });
                if (data?.email) setPrefillEmail(data.email);
            })
            .catch((err) => {
                const status = err?.response?.status;
                setReuploadStatus({ state: status === 410 ? "expired" : "invalid", info: null });
            });
    }, [isReupload, reuploadToken, reuploadEmail]);

    const onFile = (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Please upload a PDF file.");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("File too large. Max 8 MB.");
            return;
        }
        setResumeFile(file);
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer?.files?.[0];
        if (f) onFile(f);
    };

    const onOptimizerFile = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Please upload a PDF, or paste your resume text.");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        try {
            setExtracting(true);
            const { data } = await api.post("/ai-resume-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResumeText(data.text);
            toast.success(`Extracted ${data.chars.toLocaleString()} characters from your resume.`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setExtracting(false);
            if (optimizerFileRef.current) optimizerFileRef.current.value = "";
        }
    };

    const submitResume = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        if (!resumeFile) {
            toast.error("Please upload your resume (PDF) before submitting.");
            return;
        }
        const prefs = Object.entries(jobPrefs).filter(([, v]) => v).map(([k]) => k).join(",");
        const email = fd.get("email") || "";

        const out = new FormData();
        out.append("name", fd.get("name") || "");
        out.append("email", email);
        out.append("phone", fd.get("phone") || "");
        out.append("city", fd.get("city") || "");
        out.append("desired_position", fd.get("desired_position") || "");
        out.append("years_experience", yearsExp);
        out.append("languages_spoken", fd.get("languages_spoken") || "");
        out.append("linkedin_url", fd.get("linkedin_url") || "");
        out.append("work_authorization", workAuth);
        out.append("desired_salary", fd.get("desired_salary") || "");
        out.append("availability", availability);
        out.append("job_type_preferences", prefs);
        out.append("notes", fd.get("notes") || "");
        out.append("resume", resumeFile);

        if (!out.get("name") || !out.get("email")) {
            toast.error("Name and email are required.");
            return;
        }

        try {
            setUploadLoading(true);
            await api.post("/resume-upload", out, { headers: { "Content-Type": "multipart/form-data" } });
            e.target.reset();
            setResumeFile(null);
            setWorkAuth("");
            setAvailability("");
            setYearsExp("");
            setJobPrefs({ Onsite: false, Hybrid: false, Remote: false });
            if (user && user.role === "professional") {
                toast.success("Talent Passport updated — Simonara AI is refreshing your dashboard.");
                navigate("/professional/dashboard");
            } else {
                toast.success("Talent Passport created! Set up your account to unlock your AI Career Dashboard.");
                navigate(`/signup?role=professional&email=${encodeURIComponent(email)}`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploadLoading(false);
        }
    };

    const runAiReview = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const body = {
            resume_text: resumeText || fd.get("resume_text") || "",
            target_role: fd.get("target_role") || "",
        };
        if (!body.resume_text || body.resume_text.length < 50) {
            toast.error("Paste or upload your resume so we can review it.");
            return;
        }
        try {
            setAiLoading(true);
            setAiResult(null);
            const { data } = await api.post("/ai-resume-review", body);
            setAiResult(data);
            toast.success("AI analysis complete. Scroll down for your results.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAiLoading(false);
        }
    };

    return (
        <div className="bg-background">
            {/* HERO */}
            <ErrorBoundary sectionName="job-seekers-hero">
                <section className="relative overflow-hidden pt-20 pb-20 lg:pt-28">
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-emerald-500/10 blur-3xl" />
                    </div>
                    <div className="relative container-page max-w-3xl mx-auto text-center">
                        <p className="overline">For Talent · Get Discovered</p>
                        <h1 className="mt-4 font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-foreground leading-[1.05]">
                            Build your Talent Passport once. <span className="text-brand-green dark:text-emerald-400">Get discovered by employers.</span>
                        </h1>
                        <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                            No more cover letters. No more "we'll be in touch." One verified profile that puts you in front of every employer hiring on Simonara — with AI matching, resume coaching, and interview prep included for free.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                            <Link to="/talent/signup" data-testid="js-hero-build-passport" className="btn-green w-full sm:w-auto">Build Your Talent Passport</Link>
                            <Link to="/careers" data-testid="js-hero-open-roles" className="btn-outline w-full sm:w-auto">Browse Open Roles</Link>
                        </div>
                    </div>
                </section>
            </ErrorBoundary>

            {/* SOCIAL PROOF — edit /config/socialProof.js */}
            <SocialProof />

            {/* WHY THIS EXISTS — 3 benefit blocks */}
            <section className="section-pad" data-testid="js-benefits-strip">
                <div className="container-page">
                    <div className="max-w-2xl">
                        <p className="overline">Why Simonara for talent</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                            Three reasons your career belongs here.
                        </h2>
                    </div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            {
                                icon: Send,
                                title: "Stop applying to the same job, over and over.",
                                body: "Your Talent Passport lives on the platform. Every Simonara employer sees you the moment their role goes live — no more copy-paste applications or ATS black holes.",
                                testId: "js-benefit-stop-applying",
                            },
                            {
                                icon: Sparkles,
                                title: "Free AI resume optimizer and interview coach.",
                                body: "Score your resume against any role. Get a rewrite in your voice. Run realistic mock interviews and get scored on structure, impact, and specifics — all included with your passport.",
                                testId: "js-benefit-free-ai-tools",
                            },
                            {
                                icon: ShieldCheck,
                                title: "You control every verification.",
                                body: "Identity, employment, references — verified only when you're ready. Every check is opt-in, every data point is yours to revoke. Employers see the check, never the raw file.",
                                testId: "js-benefit-control-verification",
                            },
                        ].map((b) => (
                            <div
                                key={b.title}
                                className="card-soft p-7 h-full"
                                data-testid={b.testId}
                            >
                                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                                    <b.icon size={20} />
                                </div>
                                <h3 className="mt-5 font-heading font-semibold text-lg text-foreground leading-snug">
                                    {b.title}
                                </h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    {b.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 1: TALENT PASSPORT */}
            <section className="section-pad bg-muted/50" data-testid="js-passport-section">
                <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6">
                        <p className="overline">Talent Passport™</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                            One Talent Passport. Unlimited Opportunities.
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Your Talent Passport becomes your verified professional identity. Instead of filling out the same
                            information every time you apply, build your profile once and let Simonara AI do the heavy lifting.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {passportContents.map((c) => (
                                <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground">
                                    <BadgeCheck size={12} className="text-emerald-500" /> {c}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {PASSPORT_PEOPLE.slice(0, 2).map((p) => (
                                <motion.div key={p.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                                    <PassportCard person={p} />
                                </motion.div>
                            ))}
                            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="sm:col-span-2 rounded-xl border border-emerald-500/30 bg-accent p-4 text-sm text-accent-foreground dark:text-emerald-300 flex items-center gap-2.5">
                                <IdCard size={17} className="text-emerald-500 shrink-0" />
                                Your card could be next — discovered by employers, not the other way around.
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 2: AI RESUME OPTIMIZER */}
            <section id="resume-optimizer" className="section-pad" data-testid="js-optimizer-section">
                <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="overline">AI Resume Optimizer</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl lg:text-4xl tracking-tight text-foreground">
                            Optimize Your Resume in Under a Minute
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Upload or paste your resume and Simonara AI will instantly analyze it for recruiter impact — not just ATS keywords.
                        </p>
                        <ul className="mt-6 space-y-2.5 text-sm text-foreground">
                            {["Resume Score (0–100)", "ATS Compatibility", "Strengths", "Improvement Opportunities", "Tailored Recommendations", "Suggested Keywords"].map((f) => (
                                <li key={f} className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" /> {f}</li>
                            ))}
                        </ul>
                    </div>

                    <form onSubmit={runAiReview} className="lg:col-span-7 card-soft p-8 space-y-5">
                        <div>
                            <Label htmlFor="target_role">Target role <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Input id="target_role" name="target_role" data-testid={TID.jobSeekers.aiRole} placeholder="e.g. Senior HR Manager" className="mt-2" />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label htmlFor="resume_text">Your resume</Label>
                                <button type="button" onClick={() => optimizerFileRef.current?.click()} disabled={extracting}
                                    data-testid="js-optimizer-upload-btn"
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-green dark:text-emerald-400 hover:underline disabled:opacity-60">
                                    {extracting ? <><Loader2 size={12} className="animate-spin" /> Reading PDF…</> : <><FileUp size={12} /> Upload PDF</>}
                                </button>
                                <input ref={optimizerFileRef} type="file" accept="application/pdf,.pdf" onChange={(e) => onOptimizerFile(e.target.files?.[0])} data-testid="js-optimizer-file-input" className="hidden" />
                            </div>
                            <Textarea
                                id="resume_text" name="resume_text" rows={9}
                                value={resumeText} onChange={(e) => setResumeText(e.target.value)}
                                data-testid={TID.jobSeekers.aiResumeText}
                                placeholder="Paste your resume text here — or upload a PDF above…"
                                className="mt-2 font-mono text-sm"
                            />
                        </div>
                        <div>
                            <button type="submit" disabled={aiLoading} data-testid={TID.jobSeekers.aiSubmit} className="btn-green w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed">
                                {aiLoading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Analyzing…</> : <><Sparkles size={17} className="mr-2" /> Optimize Resume</>}
                            </button>
                            <p className="mt-2 text-xs text-muted-foreground">Free. No signup required.</p>
                        </div>

                        {aiResult && (
                            <div data-testid={TID.jobSeekers.aiResult} className="mt-6 border-t border-border pt-6 space-y-5">
                                <div className="flex items-center gap-5">
                                    <div className="w-20 h-20 rounded-2xl bg-brand-navy dark:bg-slate-800 text-white flex flex-col items-center justify-center">
                                        <span className="mono-score text-2xl font-bold">{aiResult.overall_score ?? "—"}</span>
                                        <span className="text-[10px] uppercase tracking-widest text-white/60">Score</span>
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm text-muted-foreground">ATS Compatibility</div>
                                        <div className="mono-score text-2xl font-bold text-foreground">{aiResult.ats_compatibility?.score ?? "—"}<span className="text-base text-muted-foreground">/100</span></div>
                                        <div className="text-sm text-muted-foreground mt-1">{aiResult.ats_compatibility?.notes}</div>
                                    </div>
                                </div>
                                <p className="text-foreground leading-relaxed bg-muted rounded-xl p-4 text-sm">{aiResult.summary}</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <h4 className="font-heading font-semibold text-foreground mb-2">Strengths</h4>
                                        <ul className="space-y-2 text-sm">
                                            {(aiResult.strengths || []).map((s, i) => (<li key={i} className="flex gap-2 text-foreground"><span className="text-emerald-500">✓</span>{s}</li>))}
                                        </ul>
                                    </div>
                                    <div>
                                        <h4 className="font-heading font-semibold text-foreground mb-2">Improvement Opportunities</h4>
                                        <ul className="space-y-2 text-sm">
                                            {(aiResult.weaknesses || []).map((s, i) => (<li key={i} className="flex gap-2 text-muted-foreground"><AlertCircle size={14} className="mt-1 shrink-0" />{s}</li>))}
                                        </ul>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-heading font-semibold text-foreground mb-2">Tailored Recommendations</h4>
                                    <ul className="space-y-2 text-sm">
                                        {(aiResult.suggestions || []).map((s, i) => (
                                            <li key={i} className="flex gap-2 bg-accent/60 p-3 rounded-lg text-foreground"><Sparkles size={14} className="text-emerald-500 mt-1 shrink-0" />{s}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </section>

            {/* SECTION 3: AI CAREER COACH */}
            <section className="section-pad bg-brand-navy dark:bg-slate-950 text-white" data-testid="js-coach-section">
                <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <p className="overline">Simonara Coach™</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight">
                            Your Career Coach — Available 24/7
                        </h2>
                        <ul className="mt-6 space-y-2.5">
                            {coachFeatures.map((f) => (
                                <li key={f.label} className="flex items-center gap-2.5 text-sm text-white/85">
                                    <f.icon size={15} className="text-emerald-400 shrink-0" /> {f.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-7">
                        <div className="app-window !bg-slate-900 !border-white/10">
                            <div className="app-window-bar !bg-white/5 !border-white/10">
                                <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                                <span className="ml-3 text-[11px] mono-score text-white/50">Simonara Coach™</span>
                            </div>
                            <div className="p-5 space-y-3">
                                {coachChat.map((m, i) => (
                                    <motion.div
                                        key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                                        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.from === "ai" ? "bg-white/10 text-white/90" : "bg-emerald-600 text-white ml-auto"}`}
                                    >
                                        {m.from === "ai" && <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-400 mb-1"><Sparkles size={10} /> Simonara AI</span>}
                                        {m.text}
                                    </motion.div>
                                ))}
                                <div className="flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white/40">
                                    Ask your career coach anything… <Send size={14} className="ml-auto text-emerald-400" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 4: GET DISCOVERED */}
            <section className="section-pad" data-testid="js-discovered-section">
                <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6">
                        <p className="overline">Get Discovered</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                            Let Employers Find You
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Once your Talent Passport is complete, employers using Simonara can discover your profile based on
                            skills, experience, certifications, languages, career preferences, and verifications — not just keywords.
                        </p>
                        <p className="mt-3 font-medium text-foreground">No more completing the same application over and over.</p>
                    </div>
                    <div className="md:col-span-6">
                        <div className="rounded-2xl border border-border bg-card p-6">
                            <div className="flex items-center gap-2 text-sm font-heading font-semibold text-foreground">
                                <Eye size={16} className="text-emerald-500" /> Employer search — live matches
                            </div>
                            <div className="mt-4 space-y-2.5">
                                {PASSPORT_PEOPLE.slice(0, 3).map((p, i) => (
                                    <motion.div key={p.name} initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}>
                                        <PassportCard person={p} compact />
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 5: VERIFICATIONS */}
            <section className="section-pad bg-muted/50" data-testid="js-trust-section">
                <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6">
                        <p className="overline">Verifications</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                            Build Trust Before Your First Interview
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Your verification checklist updates automatically as each check is completed. Verification is optional —
                            and only employers requesting premium verification pay for verification services.
                        </p>
                    </div>
                    <div className="md:col-span-6">
                        <div className="rounded-2xl border border-border bg-card p-8">
                            <h3 className="font-heading font-semibold text-lg text-foreground">Your verifications</h3>
                            <ul className="mt-5 space-y-3">
                                {trustChecks.map((c, i) => (
                                    <motion.li key={c} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex items-center gap-2.5 text-sm text-foreground">
                                        <ShieldCheck size={16} className="text-emerald-500 shrink-0" /> {c} verified
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="section-pad" data-testid="js-benefits-section">
                <div className="container-page">
                    <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground max-w-xl">
                        Why Build a Talent Passport?
                    </h2>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {benefits.map((b, i) => (
                            <motion.div key={b} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                                className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
                                <CheckCircle2 size={17} className="text-emerald-500 shrink-0" />
                                <span className="text-sm font-medium text-foreground">{b}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section-pad" data-testid="js-faq-section">
                <div className="container-page max-w-3xl">
                    <div>
                        <p className="overline">Common questions</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                            Straight answers, no fine print.
                        </h2>
                    </div>
                    <div className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
                        {[
                            {
                                q: "Is it really free?",
                                a: "Yes. Building a Talent Passport, running your resume through AI coaching, and getting matched to roles are all free for professionals — no trials, no hidden tiers. Simonara charges employers to hire; we never charge you to work.",
                                testId: "js-faq-free",
                            },
                            {
                                q: "Do I need verification to get matched?",
                                a: "No. You can build a passport and get discovered without a single check. Verifications (identity, employment, references) are 100% optional — but each one adds a signal employers can filter on.",
                                testId: "js-faq-verification",
                            },
                            {
                                q: "Who sees my data?",
                                a: "Only employers you allow. Your passport is private by default. Employers with active roles can see the summary; the full profile unlocks only when they request access and you approve. We never sell your data — full stop.",
                                testId: "js-faq-privacy",
                            },
                        ].map((f) => (
                            <details
                                key={f.q}
                                className="group p-6"
                                data-testid={f.testId}
                            >
                                <summary className="flex items-center justify-between cursor-pointer list-none font-heading font-semibold text-foreground text-base">
                                    {f.q}
                                    <span className="ml-4 text-emerald-500 transition-transform group-open:rotate-45 text-xl leading-none select-none">+</span>
                                </summary>
                                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* PASSPORT FORM */}
            <section id="passport-form" className="section-pad bg-muted/50">
                <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        {isReupload && (
                            <ErrorBoundary sectionName="reupload-banner">
                                <div data-testid="reupload-banner" className={`mb-6 rounded-2xl border p-5 flex gap-3 ${
                                    reuploadStatus.state === "expired" || reuploadStatus.state === "invalid"
                                        ? "border-destructive/40 bg-destructive/5"
                                        : "border-emerald-500/30 bg-accent"
                                }`}>
                                <RotateCw size={20} className={`shrink-0 mt-0.5 ${reuploadStatus.state === "expired" || reuploadStatus.state === "invalid" ? "text-destructive" : "text-emerald-500"}`} />
                                <div>
                                    {reuploadStatus.state === "expired" ? (
                                        <>
                                            <h4 data-testid="reupload-banner-title" className="font-heading font-semibold text-destructive">This re-upload link has expired.</h4>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                For your security, re-upload links expire after 30 days. Please reach out via our <Link to="/contact" className="text-foreground font-semibold underline">contact form</Link> and we'll send you a fresh link.
                                            </p>
                                        </>
                                    ) : reuploadStatus.state === "invalid" ? (
                                        <>
                                            <h4 data-testid="reupload-banner-title" className="font-heading font-semibold text-destructive">This re-upload link isn't valid.</h4>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                It may have been mistyped or already been rotated. Please reach out and we'll help you get a new one.
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <h4 data-testid="reupload-banner-title" className="font-heading font-semibold text-foreground">Welcome back — please re-upload your resume.</h4>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                We've upgraded how we store resumes so we can serve you better. Please re-upload your PDF (and feel free to refresh any details) so your file stays on record.
                                            </p>
                                        </>
                                    )}
                                </div>
                                </div>
                            </ErrorBoundary>
                        )}
                        <p className="overline">{isReupload ? "Re-upload" : "Build Your Talent Passport"}</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl lg:text-4xl tracking-tight text-foreground">
                            {isReupload ? "Re-upload your resume." : "Start your Talent Passport. Get discovered."}
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Simonara AI immediately begins building your Talent Passport. Once complete, you'll unlock AI career
                            tools and become discoverable by employers using the platform.
                        </p>
                        <div data-testid="js-ai-indicator" className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-accent px-4 py-2 text-sm font-medium text-accent-foreground dark:text-emerald-300">
                            <Zap size={15} className="text-emerald-500" />
                            Your AI Career Dashboard unlocks right after submission.
                        </div>
                        <ul className="mt-6 space-y-2 text-sm text-foreground">
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5" /> Your data is private — never sold.</li>
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5" /> Bilingual support — English & Español.</li>
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5" /> Matched proactively to new openings.</li>
                        </ul>
                    </div>

                    <form onSubmit={submitResume} className="lg:col-span-7 card-soft p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <Label htmlFor="name">Full name *</Label>
                            <Input id="name" name="name" data-testid={TID.jobSeekers.uploadName} required className="mt-2" />
                        </div>
                        <div>
                            <Label htmlFor="email">Email *</Label>
                            <Input
                                id="email" name="email" type="email"
                                defaultValue={prefillEmail} key={prefillEmail || "fresh"}
                                data-testid={TID.jobSeekers.uploadEmail} required className="mt-2"
                            />
                        </div>
                        <div>
                            <Label htmlFor="phone">Phone</Label>
                            <Input id="phone" name="phone" data-testid={TID.jobSeekers.uploadPhone} className="mt-2" />
                        </div>
                        <div>
                            <Label htmlFor="city">City</Label>
                            <Input id="city" name="city" data-testid={TID.jobSeekers.uploadCity} placeholder="e.g. Los Angeles, CA" className="mt-2" />
                        </div>
                        <div>
                            <Label htmlFor="desired_position">Desired position</Label>
                            <Input id="desired_position" name="desired_position" data-testid={TID.jobSeekers.uploadRole} placeholder="e.g. Senior HR Manager" className="mt-2" />
                        </div>
                        <div>
                            <Label>Years of experience</Label>
                            <Select value={yearsExp} onValueChange={setYearsExp}>
                                <SelectTrigger data-testid={TID.jobSeekers.uploadYears} className="mt-2">
                                    <SelectValue placeholder="Select range" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="0-1">0 – 1 years</SelectItem>
                                    <SelectItem value="2-4">2 – 4 years</SelectItem>
                                    <SelectItem value="5-7">5 – 7 years</SelectItem>
                                    <SelectItem value="8-10">8 – 10 years</SelectItem>
                                    <SelectItem value="10+">10+ years</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="sm:col-span-2">
                            <Label htmlFor="languages_spoken">Languages spoken</Label>
                            <Input id="languages_spoken" name="languages_spoken" data-testid={TID.jobSeekers.uploadLanguages} placeholder="e.g. English, Spanish, Portuguese" className="mt-2" />
                        </div>
                        <div>
                            <Label htmlFor="linkedin_url">LinkedIn profile</Label>
                            <Input id="linkedin_url" name="linkedin_url" type="url" data-testid={TID.jobSeekers.uploadLinkedin} placeholder="https://linkedin.com/in/..." className="mt-2" />
                        </div>
                        <div>
                            <Label>Work authorization <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Select value={workAuth} onValueChange={setWorkAuth}>
                                <SelectTrigger data-testid={TID.jobSeekers.uploadWorkAuth} className="mt-2">
                                    <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="US Citizen">U.S. Citizen</SelectItem>
                                    <SelectItem value="Permanent Resident">Permanent Resident / Green Card</SelectItem>
                                    <SelectItem value="Work Visa">Work Visa (H-1B, TN, etc.)</SelectItem>
                                    <SelectItem value="Need Sponsorship">Authorized — Need Sponsorship</SelectItem>
                                    <SelectItem value="Other">Other</SelectItem>
                                    <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="desired_salary">Desired salary</Label>
                            <Input id="desired_salary" name="desired_salary" data-testid={TID.jobSeekers.uploadSalary} placeholder="e.g. $65,000 – $75,000" className="mt-2" />
                        </div>
                        <div>
                            <Label>Availability</Label>
                            <Select value={availability} onValueChange={setAvailability}>
                                <SelectTrigger data-testid={TID.jobSeekers.uploadAvailability} className="mt-2">
                                    <SelectValue placeholder="When can you start?" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Immediately">Immediately</SelectItem>
                                    <SelectItem value="2 weeks">In 2 weeks</SelectItem>
                                    <SelectItem value="1 month">In 1 month</SelectItem>
                                    <SelectItem value="2-3 months">2 – 3 months</SelectItem>
                                    <SelectItem value="Just exploring">Just exploring</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="sm:col-span-2">
                            <Label>Preferred job type <span className="text-muted-foreground font-normal">(select all that apply)</span></Label>
                            <div className="mt-3 grid grid-cols-3 gap-3">
                                {["Onsite", "Hybrid", "Remote"].map((opt) => {
                                    const tid = opt === "Onsite" ? TID.jobSeekers.uploadJobOnsite : opt === "Hybrid" ? TID.jobSeekers.uploadJobHybrid : TID.jobSeekers.uploadJobRemote;
                                    const checked = jobPrefs[opt];
                                    return (
                                        <label
                                            key={opt}
                                            className={`flex items-center gap-3 cursor-pointer rounded-xl border p-3 transition-colors ${
                                                checked ? "border-emerald-500 bg-accent" : "border-border bg-card hover:border-emerald-500/40"
                                            }`}
                                        >
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={(v) => setJobPrefs((p) => ({ ...p, [opt]: !!v }))}
                                                data-testid={tid}
                                            />
                                            <span className="text-sm font-medium text-foreground">{opt}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="sm:col-span-2">
                            <Label>Resume upload (PDF) *</Label>
                            <div
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={onDrop}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid={TID.jobSeekers.uploadResumeFileLabel}
                                className={`mt-2 cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
                                    resumeFile ? "border-emerald-500 bg-accent" : "border-border bg-muted/50 hover:border-emerald-500/50"
                                }`}
                            >
                                {resumeFile ? (
                                    <div className="flex items-center justify-center gap-3">
                                        <FileText size={22} className="text-emerald-500" />
                                        <div className="text-left">
                                            <div className="font-semibold text-foreground text-sm">{resumeFile.name}</div>
                                            <div className="text-xs text-muted-foreground">{(resumeFile.size / 1024).toFixed(0)} KB · Click to replace</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setResumeFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                                            className="ml-3 p-1 rounded-full hover:bg-card"
                                            aria-label="Remove file"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                        <FileUp size={26} className="text-foreground" />
                                        <div className="text-sm font-medium text-foreground">Drop your PDF here, or click to browse</div>
                                        <div className="text-xs">PDF only · Max 8 MB · Simonara AI extracts the text automatically</div>
                                    </div>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    onChange={(e) => onFile(e.target.files?.[0])}
                                    data-testid={TID.jobSeekers.uploadResumeFile}
                                    className="hidden"
                                />
                            </div>
                        </div>

                        <div className="sm:col-span-2">
                            <Label htmlFor="notes">Anything else we should know?</Label>
                            <Textarea id="notes" name="notes" data-testid={TID.jobSeekers.uploadNotes} rows={3} className="mt-2" />
                        </div>
                        <div className="sm:col-span-2">
                            <button type="submit" disabled={uploadLoading} data-testid={TID.jobSeekers.uploadSubmit} className="btn-green disabled:opacity-60">
                                {uploadLoading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Building your passport…</> : <><Upload size={18} className="mr-2" /> Build My Talent Passport</>}
                            </button>
                            <p className="mt-3 text-xs text-muted-foreground">
                                Powered by Simonara AI. Enhanced by experienced coaches when you need them.
                            </p>
                        </div>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default JobSeekers;
